import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { FileText, Loader2, Receipt, Search } from 'lucide-react';
import { toast } from 'sonner';
import { patientApi } from '@/api/patient';
import { billingApi } from '@/api/billing';
import { ipdBillingApi } from '@/api/ipdBilling';
import type { BillWiseCategory, BillWiseRow } from '@/api/businessReports';
import { downloadBlob } from '@/lib/downloadBlob';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { Patient } from '@/types/types';
import ViewBill from '@/components/billing/ViewBill';
import { BillStatus } from '@/types/types';

const CATEGORY_OPTIONS: { value: BillWiseCategory; label: string }[] = [
  { value: 'All', label: 'All categories' },
  { value: 'OPD', label: 'OPD' },
  { value: 'Lab', label: 'Lab' },
  { value: 'Pharma', label: 'Pharma' },
  { value: 'Day Care', label: 'Daycare' },
  { value: 'Additional Services', label: 'Additional Services' },
  { value: 'IPD', label: 'IPD' },
  { value: 'ICU', label: 'ICU' },
  { value: 'IPD Pharma', label: 'IPD Pharma' },
];

function isIpdFinalBillCategory(category: string): boolean {
  return category === 'IPD' || category === 'ICU';
}

function isOutpatientBillRow(category: string): boolean {
  return !isIpdFinalBillCategory(category);
}

function isPhoneQuery(term: string): boolean {
  const digits = term.replace(/\D/g, '');
  return digits.length >= 10 && digits.length === term.replace(/\s/g, '').length;
}

export default function PatientBillsPage() {
  const queryClient = useQueryClient();
  const [searchInput, setSearchInput] = useState('');
  const [searchResults, setSearchResults] = useState<Patient[]>([]);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [category, setCategory] = useState<BillWiseCategory>('All');
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [openBillId, setOpenBillId] = useState<string | null>(null);
  const [viewBillOpen, setViewBillOpen] = useState(false);

  const { data: billReport, isLoading: billsLoading } = useQuery({
    queryKey: ['receptionist-patient-bills', selectedPatient?.id, category],
    queryFn: () => billingApi.getPatientAllBills(selectedPatient!.id, { category }),
    enabled: !!selectedPatient?.id,
  });

  const runSearch = async () => {
    const term = searchInput.trim();
    if (!term) {
      toast.error('Enter a patient name or phone number');
      return;
    }
    setSearchLoading(true);
    setSelectedPatient(null);
    try {
      const results = isPhoneQuery(term)
        ? await patientApi.getPatientByPhone(term.replace(/\D/g, ''))
        : await patientApi.getPatientByName(term);
      const list = results ?? [];
      setSearchResults(list);
      if (list.length === 0) {
        toast.info('No patients found');
      } else if (list.length === 1) {
        setSelectedPatient(list[0]);
        setSearchResults([]);
      }
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      toast.error(message || 'Patient search failed');
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSelectPatient = (patient: Patient) => {
    setSelectedPatient(patient);
    setSearchResults([]);
  };

  const handleDownloadPdf = async (row: BillWiseRow) => {
    setDownloadingId(row.billId);
    try {
      if (isIpdFinalBillCategory(row.billCategory)) {
        if (!row.admissionId) {
          toast.error('IPD admission not found for this bill');
          return;
        }
        const resp = await ipdBillingApi.getFinalBillPDF(row.admissionId);
        downloadBlob(
          new Blob([resp.data], { type: 'application/pdf' }),
          `IPD-Bill-${row.billNumber || row.ipdNumber || row.billId}.pdf`
        );
      } else {
        const resp = await billingApi.exportBillPDF(row.billId);
        downloadBlob(
          new Blob([resp.data], { type: 'application/pdf' }),
          `Bill-${row.billNumber || row.billId}.pdf`
        );
      }
      toast.success('Bill PDF downloaded');
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      toast.error(message || 'Failed to download bill PDF');
    } finally {
      setDownloadingId(null);
    }
  };

  const bills = billReport?.bills ?? [];
  const totals = billReport?.totals;

  return (
    <div className="thms-page-shell">
      <div>
        <h1 className="thms-page-title flex items-center gap-2">
          <Receipt className="w-8 h-8 text-blue-600" />
          Patient Bills
        </h1>
        <p className="thms-page-subtitle">
          Search by patient name or phone number to view and download all OPD and IPD bills.
        </p>
      </div>

      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="pl-9"
                placeholder="Patient name or 10-digit phone number"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && runSearch()}
              />
            </div>
            <Button onClick={runSearch} disabled={searchLoading}>
              {searchLoading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Search className="mr-2 h-4 w-4" />
              )}
              Search
            </Button>
          </div>

          {searchResults.length > 0 && (
            <div className="rounded-md border divide-y">
              {searchResults.map((patient) => (
                <button
                  key={patient.id}
                  type="button"
                  className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-muted/50"
                  onClick={() => handleSelectPatient(patient)}
                >
                  <span className="font-medium">{patient.name}</span>
                  <span className="text-sm text-muted-foreground">
                    {patient.phone}
                    {patient.uhid ? ` · UHID ${patient.uhid}` : ''}
                  </span>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {selectedPatient && (
        <>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <span>{selectedPatient.name}</span>
                <span className="text-sm font-normal text-muted-foreground">
                  {selectedPatient.phone}
                  {selectedPatient.uhid ? ` · UHID ${selectedPatient.uhid}` : ''}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="ml-auto"
                  onClick={() => {
                    setSelectedPatient(null);
                    setSearchResults([]);
                  }}
                >
                  Change patient
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Select value={category} onValueChange={(v) => setCategory(v as BillWiseCategory)}>
                <SelectTrigger className="w-[220px]">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORY_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {totals && (
                <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
                  {[
                    { label: 'Bills', value: totals.count, currency: false },
                    { label: 'Net amount', value: totals.netAmount, currency: true },
                    { label: 'Paid amount', value: totals.paidAmount, currency: true },
                  ].map((t) => (
                    <div key={t.label} className="rounded-lg border p-3">
                      <p className="text-xs text-muted-foreground">{t.label}</p>
                      <p className="mt-1 text-lg font-semibold tabular-nums">
                        {t.currency
                          ? `₹${t.value.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`
                          : t.value.toLocaleString('en-IN')}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {billsLoading ? (
            <p className="text-sm text-muted-foreground">Loading bills…</p>
          ) : bills.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                No bills found for this patient.
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="pt-6">
                <div className="rounded-md border overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Category</TableHead>
                        <TableHead>Bill no.</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead className="text-right">Net</TableHead>
                        <TableHead className="text-right">Paid</TableHead>
                        <TableHead className="text-right">Due</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>IPD no.</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {bills.map((row) => (
                        <TableRow key={`${row.billCategory}-${row.billId}`}>
                          <TableCell className="whitespace-nowrap">{row.billCategory}</TableCell>
                          <TableCell className="font-mono text-xs whitespace-nowrap">
                            {row.billNumber}
                          </TableCell>
                          <TableCell className="whitespace-nowrap">
                            {format(new Date(row.billDate), 'dd/MM/yyyy')}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            ₹{row.netAmount.toLocaleString('en-IN')}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            ₹{row.paidAmount.toLocaleString('en-IN')}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            ₹{row.dueAmount.toLocaleString('en-IN')}
                          </TableCell>
                          <TableCell className="whitespace-nowrap">
                            <span
                              className={
                                row.status === BillStatus.DRAFT
                                  ? 'rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-900'
                                  : ''
                              }
                            >
                              {row.status}
                            </span>
                          </TableCell>
                          <TableCell className="whitespace-nowrap text-muted-foreground">
                            {row.ipdNumber || '—'}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex flex-wrap justify-end gap-1">
                              {isOutpatientBillRow(row.billCategory) && (
                                <Button
                                  variant="default"
                                  size="sm"
                                  onClick={() => {
                                    setOpenBillId(row.billId);
                                    setViewBillOpen(true);
                                  }}
                                >
                                  {row.status === BillStatus.DRAFT ? 'Open draft' : 'Open'}
                                </Button>
                              )}
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={
                                  downloadingId === row.billId ||
                                  (isIpdFinalBillCategory(row.billCategory) && !row.admissionId)
                                }
                                onClick={() => handleDownloadPdf(row)}
                              >
                                {downloadingId === row.billId ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <>
                                    <FileText className="mr-1.5 h-4 w-4" />
                                    PDF
                                  </>
                                )}
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      <ViewBill
        billId={openBillId}
        isOpen={viewBillOpen}
        onClose={() => {
          setViewBillOpen(false);
          setOpenBillId(null);
        }}
        onSuccess={() => {
          queryClient.invalidateQueries({
            queryKey: ['receptionist-patient-bills', selectedPatient?.id, category],
          });
        }}
      />
    </div>
  );
}
